import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, FileText, MessageSquare, Calendar, TrendingUp, AlertCircle, Mail, Send } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { QueueStatusWidget } from '@/components/dashboard/QueueStatusWidget';
import NewClientModal from '@/components/clients/NewClientModal';
import { useAuthStore } from '@/store/authStore';
import { billingApi, euro } from '@/services/billingApi';

// Dashboard stats will be loaded from API
interface DashboardStats {
  totalClients: number;
  activePractices: number;
  totalPractices: number;
  openPractices: number;
  closedPractices: number;
  whatsappMessages: number;
  emailsReceived: number;
  emailsSent: number;
  todayAppointments: number;
  upcomingAppointments: number;
  clientsChange: string;
  practicesChange: string;
  practicesOpenChange: string;
  practicesClosedChange: string;
  whatsappChange: string;
  emailsReceivedChange: string;
  emailsSentChange: string;
  appointmentsChange: string;
  appointmentsUpcomingChange: string;
}

interface RecentActivity {
  id: string;
  type: 'client' | 'whatsapp' | 'email' | 'practice' | 'appointment' | 'document';
  title: string;
  description: string;
  timeAgo: string;
  icon: string;
}

interface UrgentActivity {
  id: string;
  title: string;
  description: string;
  priority: 'Alta' | 'Urgente' | 'Media';
  dueDate: string;
  type: 'deadline' | 'whatsapp' | 'completion';
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [billingStats, setBillingStats] = useState<{ outstanding: number; overdue: number; issuedMonth: number; collectedMonth: number } | null>(null);
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [urgentActivities, setUrgentActivities] = useState<UrgentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewClientModal, setShowNewClientModal] = useState(false);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [statsResponse, activitiesResponse, urgentResponse] = await Promise.all([
          fetch('/api/dashboard/stats'),
          fetch('/api/dashboard/activities/recent'),
          fetch('/api/dashboard/activities/urgent')
        ]);

        if (!statsResponse.ok || !activitiesResponse.ok || !urgentResponse.ok) {
          throw new Error('Errore nel caricamento dei dati dashboard');
        }

        const [statsData, activitiesData, urgentData] = await Promise.all([
          statsResponse.json(),
          activitiesResponse.json(),
          urgentResponse.json()
        ]);

        if (statsData.success) {
          setStats(statsData.data);
        }

        if (activitiesData.success) {
          setRecentActivities(activitiesData.data);
        }

        if (urgentData.success) {
          setUrgentActivities(urgentData.data);
        }
        if (user?.canAccessQuotesAndAccounts) {
          try {
            setBillingStats(await billingApi.stats());
          } catch {
            setBillingStats(null);
          }
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(err instanceof Error ? err.message : 'Errore sconosciuto');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user?.canAccessQuotesAndAccounts]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Panoramica delle attività del tuo studio
          </p>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Caricamento...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Panoramica delle attività del tuo studio
          </p>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-red-500">Errore: {error}</div>
        </div>
      </div>
    );
  }

  // Create stats array from API data
  const statsCards = stats ? [
    {
      name: 'Clienti Totali',
      value: stats.totalClients.toString(),
      change: stats.clientsChange,
      changeType: stats.clientsChange.startsWith('+') ? 'increase' : 'decrease',
      icon: Users,
    },
    {
      name: 'Pratiche Totali',
      value: stats.totalPractices.toString(),
      change: stats.practicesChange,
      changeType: stats.practicesChange.startsWith('+') ? 'increase' : 'decrease',
      icon: FileText,
    },
    {
      name: 'Pratiche Aperte',
      value: stats.openPractices.toString(),
      change: stats.practicesOpenChange || '0.0%',
      changeType: (stats.practicesOpenChange || '0.0%').startsWith('+') ? 'increase' : 'decrease',
      icon: FileText,
    },
    {
      name: 'Pratiche Chiuse',
      value: stats.closedPractices.toString(),
      change: stats.practicesClosedChange || '0.0%',
      changeType: (stats.practicesClosedChange || '0.0%').startsWith('+') ? 'increase' : 'decrease',
      icon: FileText,
    },
    {
      name: 'Messaggi WhatsApp',
      value: stats.whatsappMessages.toString(),
      change: stats.whatsappChange,
      changeType: stats.whatsappChange.startsWith('+') ? 'increase' : 'decrease',
      icon: MessageSquare,
    },
    {
      name: 'Email Ricevute',
      value: stats.emailsReceived.toString(),
      change: stats.emailsReceivedChange,
      changeType: stats.emailsReceivedChange.startsWith('+') ? 'increase' : 'decrease',
      icon: Mail,
    },
    {
      name: 'Email Inviate',
      value: stats.emailsSent.toString(),
      change: stats.emailsSentChange,
      changeType: stats.emailsSentChange.startsWith('+') ? 'increase' : 'decrease',
      icon: Send,
    },
    {
      name: 'Appuntamenti Oggi',
      value: stats.todayAppointments.toString(),
      change: stats.appointmentsChange,
      changeType: stats.appointmentsChange.startsWith('+') ? 'increase' : 'decrease',
      icon: Calendar,
    },
    {
      name: 'Prossimi Appuntamenti',
      value: stats.upcomingAppointments.toString(),
      change: stats.appointmentsUpcomingChange || '0.0%',
      changeType: (stats.appointmentsUpcomingChange || '0.0%').startsWith('+') ? 'increase' : 'decrease',
      icon: Calendar,
    },
  ] : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Panoramica delle attività del tuo studio
        </p>
      </div>

      {/* Layout con Stats a sinistra e Sistema Code a destra */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Stats - occupa 2 colonne */}
        <div className="lg:col-span-2">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
        {statsCards.map((stat) => (
          <Card key={stat.name}>
            <CardContent className="p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <stat.icon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4 w-0 flex-1">
                  <dl>
                    <dt className="text-xs font-medium text-gray-500 leading-tight min-h-[2rem] flex items-center">
                      {stat.name}
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-xl font-semibold text-gray-900">
                        {stat.value}
                      </div>
                      <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                        stat.changeType === 'increase' 
                          ? 'text-green-600' 
                          : stat.changeType === 'decrease'
                          ? 'text-red-600'
                          : 'text-gray-500'
                      }`}>
                        <TrendingUp className="h-4 w-4 flex-shrink-0 self-center" />
                        <span className="sr-only">
                          {stat.changeType === 'increase' ? 'Aumentato' : 'Diminuito'} di
                        </span>
                        {stat.change}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
          </div>
          {billingStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              {[
                { name: 'Da incassare', value: euro(billingStats.outstanding) },
                { name: 'Scaduto', value: euro(billingStats.overdue) },
                { name: 'Emesso nel mese', value: euro(billingStats.issuedMonth) },
                { name: 'Incassato nel mese', value: euro(billingStats.collectedMonth) },
              ].map((s) => (
                <Card key={s.name}>
                  <CardContent className="p-3">
                    <div className="text-xs text-gray-500">{s.name}</div>
                    <div className="text-lg font-semibold">{s.value}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Sistema Code - occupa 1 colonna a destra */}
        <div className="lg:col-span-1">
          <QueueStatusWidget compact={true} refreshInterval={10000} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Activities */}
        <Card>
          <CardHeader>
            <CardTitle>Attività Recenti</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivities.length > 0 ? (
                recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-center space-x-4">
                    <div className="flex-shrink-0 text-lg">
                      {activity.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {activity.title}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {activity.description}
                      </p>
                      <p className="text-xs text-gray-400">
                        {activity.timeAgo}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Nessuna attività recente
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Urgent Tasks */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              Attività Urgenti
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {urgentActivities.length > 0 ? (
                urgentActivities.map((task) => (
                  <div 
                    key={task.id} 
                    className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                    onClick={() => navigate(`/activities/${task.id}`)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {task.title}
                      </p>
                      <p className="text-sm text-gray-500">
                        Scadenza: {task.dueDate}
                      </p>
                    </div>
                    <div className={`flex-shrink-0 px-2 py-1 text-xs font-medium rounded-full ${
                      task.priority === 'Urgente' ? 'bg-red-100 text-red-800' :
                      task.priority === 'Alta' ? 'bg-orange-100 text-orange-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {task.priority}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Nessuna attività urgente
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions - Sticky in fondo */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-lg">
        <div className="w-full px-4 py-3 sm:px-5 lg:px-6">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <button 
              onClick={() => setShowNewClientModal(true)}
              className="flex flex-col items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors bg-white"
            >
              <Users className="h-6 w-6 text-blue-600 mb-1" />
              <span className="text-xs font-medium">Nuovo Cliente</span>
            </button>
            <button 
              onClick={() => navigate('/practices/new')}
              className="flex flex-col items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors bg-white"
            >
              <FileText className="h-6 w-6 text-green-600 mb-1" />
              <span className="text-xs font-medium">Nuova Pratica</span>
            </button>
            <button 
              onClick={() => navigate('/calendar?new=true')}
              className="flex flex-col items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors bg-white"
            >
              <Calendar className="h-6 w-6 text-purple-600 mb-1" />
              <span className="text-xs font-medium">Appuntamenti</span>
            </button>
            <button 
              onClick={() => navigate('/whatsapp')}
              className="flex flex-col items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors bg-white"
            >
              <MessageSquare className="h-6 w-6 text-orange-600 mb-1" />
              <span className="text-xs font-medium">WhatsApp</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Spacer per evitare che il contenuto sia coperto dalle azioni rapide */}
      <div className="h-24"></div>

      {/* New Client Modal */}
      <NewClientModal 
        isOpen={showNewClientModal} 
        onClose={() => setShowNewClientModal(false)} 
      />
    </div>
  );
}

// Versione con i nuovi componenti (per riferimento)
// import { DashboardStats, RecentActivities, UrgentActivities, QuickActions } from '@/components/dashboard';
//
// const dashboardStatsData = {
//   totalClients: 142,
//   activePractices: 58,
//   whatsappMessages: 23,
//   todayAppointments: 6,
//   clientsChange: '+4.75%',
//   practicesChange: '+2.15%',
//   whatsappChange: '+1.25%',
//   appointmentsChange: '+3.20%'
// };
//
// const recentActivitiesData = [
//   {
//     id: '1',
//     type: 'practice' as const,
//     title: 'Nuova pratica SCIA',
//     description: 'Mario Rossi',
//     timeAgo: '2 ore fa',
//     icon: '📋'
//   },
//   {
//     id: '2',
//     type: 'whatsapp' as const,
//     title: 'Messaggio WhatsApp',
//     description: 'Anna Verdi',
//     timeAgo: '4 ore fa',
//     icon: '💬'
//   }
// ];
//
// const urgentActivitiesData = [
//   {
//     id: '1',
//     title: 'Scadenza pratica SCIA',
//     description: 'Via Roma 123',
//     priority: 'Alta' as const,
//     dueDate: 'Domani',
//     type: 'deadline' as const
//   }
// ];
//
// const quickActionsData = [
//   {
//     id: 'new-client',
//     title: 'Nuovo Cliente',
//     icon: '👤',
//     color: 'blue',
//     route: '/clients/new'
//   }
// ];
